/**
 * usePwaInstall — PWA install prompt hook.
 *
 * The `beforeinstallprompt` event fires once, very early in the page lifecycle,
 * potentially before any React component has mounted. To avoid losing it we
 * capture it at MODULE LEVEL and maintain a subscriber set so late-mounting
 * components still receive the current state.
 *
 * All browser-API access is guarded for SSR / non-browser environments.
 */

import { useState, useEffect } from 'react';

// ---------------------------------------------------------------------------
// Module-level state — persists across hook instances
// ---------------------------------------------------------------------------

/** @type {BeforeInstallPromptEvent|null} */
let deferredPrompt = null;

/** @type {Set<() => void>} */
const subscribers = new Set();

function notifySubscribers() {
  subscribers.forEach((fn) => fn());
}

// Attach listeners once, at module load time (SSR-safe)
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e;
    notifySubscribers();
  });

  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    notifySubscribers();
  });
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getIsStandalone() {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

function getIsIOS() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  // Standard iPhone/iPad/iPod detection
  if (/iPhone|iPad|iPod/.test(ua)) return true;
  // iPadOS 13+ shows MacIntel UA with touch points
  if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) return true;
  return false;
}

function getPlatform() {
  if (typeof navigator === 'undefined') return 'desktop';
  const ua = navigator.userAgent || '';
  if (/iPhone|iPad|iPod/.test(ua)) return 'ios';
  if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) return 'ios';
  if (/Android/.test(ua)) return 'android';
  return 'desktop';
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * @returns {{
 *   canInstallDirect: boolean,
 *   isIOS: boolean,
 *   isStandalone: boolean,
 *   platform: 'ios' | 'android' | 'desktop',
 *   promptInstall: () => Promise<{ outcome: string }>,
 * }}
 */
export default function usePwaInstall() {
  // Derive initial state from module-level vars so it's correct even if the
  // event already fired before this component mounted.
  const [state, setState] = useState(() => ({
    canInstallDirect: deferredPrompt !== null && !getIsStandalone(),
    isStandalone: getIsStandalone(),
    isIOS: getIsIOS(),
    platform: getPlatform(),
  }));

  useEffect(() => {
    function handleUpdate() {
      setState({
        canInstallDirect: deferredPrompt !== null && !getIsStandalone(),
        isStandalone: getIsStandalone(),
        isIOS: getIsIOS(),
        platform: getPlatform(),
      });
    }

    subscribers.add(handleUpdate);
    return () => {
      subscribers.delete(handleUpdate);
    };
  }, []);

  async function promptInstall() {
    if (!deferredPrompt) {
      return { outcome: 'unavailable' };
    }

    const prompt = deferredPrompt;
    // The prompt is single-use — clear immediately before awaiting
    deferredPrompt = null;
    notifySubscribers();

    try {
      prompt.prompt();
      const choiceResult = await prompt.userChoice;
      return { outcome: choiceResult.outcome };
    } catch {
      return { outcome: 'unavailable' };
    }
  }

  return {
    canInstallDirect: state.canInstallDirect,
    isIOS: state.isIOS,
    isStandalone: state.isStandalone,
    platform: state.platform,
    promptInstall,
  };
}
